import React, { useState, useMemo, useEffect, useRef } from 'react';
import { CourtPOI } from '../../types';
import { getStateName } from '../../data/usLocations';
import L from 'leaflet';
import { 
  MapPin, 
  Flame, 
  Building2, 
  Users, 
  Activity, 
  Sparkles, 
  Navigation, 
  ChevronRight,
  Eye,
  ShieldCheck,
  Layers
} from 'lucide-react';

interface RegionalCoverageMapProps {
  registeredCity?: string;
  registeredState?: string;
  courts?: CourtPOI[];
  onNavigateToCourts?: () => void;
}

interface RegionalHub {
  id: string;
  cityName: string;
  lat: number;
  lng: number;
  athletesCount: number;
  courtCount: number;
  activeGames: number;
  density: 'Extreme' | 'High' | 'Moderate';
}

export const RegionalCoverageMap: React.FC<RegionalCoverageMapProps> = ({
  registeredCity = 'Venice',
  registeredState = 'CA',
  courts = [],
  onNavigateToCourts
}) => {
  const city = registeredCity || 'Venice';
  const stateCode = registeredState || 'CA';
  const stateName = getStateName(stateCode);

  const [selectedHubId, setSelectedHubId] = useState<string | null>(null);

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const leafletMapRef = useRef<L.Map | null>(null);
  const markersGroupRef = useRef<L.LayerGroup | null>(null);

  // Generate state-specific regional hubs with geographic coordinates for Leaflet tile rendering
  const regionalHubs = useMemo<RegionalHub[]>(() => {
    if (stateCode === 'NY') {
      return [
        { id: 'h1', cityName: 'New York City (NYC)', lat: 40.7128, lng: -74.0060, athletesCount: 3840, courtCount: 86, activeGames: 24, density: 'Extreme' },
        { id: 'h2', cityName: 'Buffalo', lat: 42.8864, lng: -78.8784, athletesCount: 620, courtCount: 18, activeGames: 5, density: 'Moderate' },
        { id: 'h3', cityName: 'Rochester', lat: 43.1566, lng: -77.6088, athletesCount: 480, courtCount: 14, activeGames: 3, density: 'Moderate' },
        { id: 'h4', cityName: 'Syracuse', lat: 43.0481, lng: -76.1474, athletesCount: 710, courtCount: 22, activeGames: 7, density: 'High' },
        { id: 'h5', cityName: 'Albany', lat: 42.6526, lng: -73.7562, athletesCount: 940, courtCount: 28, activeGames: 9, density: 'High' }
      ];
    } else if (stateCode === 'TX') {
      return [
        { id: 'h1', cityName: 'Houston', lat: 29.7604, lng: -95.3698, athletesCount: 2900, courtCount: 64, activeGames: 18, density: 'Extreme' },
        { id: 'h2', cityName: 'Dallas / Fort Worth', lat: 32.7767, lng: -96.7970, athletesCount: 3100, courtCount: 72, activeGames: 21, density: 'Extreme' },
        { id: 'h3', cityName: 'Austin', lat: 30.2672, lng: -97.7431, athletesCount: 1850, courtCount: 42, activeGames: 12, density: 'High' },
        { id: 'h4', cityName: 'San Antonio', lat: 29.4241, lng: -98.4936, athletesCount: 1420, courtCount: 35, activeGames: 8, density: 'High' },
        { id: 'h5', cityName: 'El Paso', lat: 31.7619, lng: -106.4850, athletesCount: 510, courtCount: 16, activeGames: 4, density: 'Moderate' }
      ];
    } else if (stateCode === 'FL') {
      return [
        { id: 'h1', cityName: 'Miami Metro', lat: 25.7617, lng: -80.1918, athletesCount: 3400, courtCount: 78, activeGames: 22, density: 'Extreme' },
        { id: 'h2', cityName: 'Orlando', lat: 28.5383, lng: -81.3792, athletesCount: 1920, courtCount: 44, activeGames: 14, density: 'High' },
        { id: 'h3', cityName: 'Tampa Bay', lat: 27.9506, lng: -82.4572, athletesCount: 1680, courtCount: 38, activeGames: 11, density: 'High' },
        { id: 'h4', cityName: 'Jacksonville', lat: 30.3322, lng: -81.6557, athletesCount: 1210, courtCount: 30, activeGames: 7, density: 'High' }
      ];
    }

    // Default CA or generic state hubs
    return [
      { id: 'h1', cityName: `${city} / Metro West`, lat: 33.9850, lng: -118.4695, athletesCount: 2850, courtCount: 62, activeGames: 19, density: 'Extreme' },
      { id: 'h2', cityName: 'Los Angeles / Venice', lat: 34.0522, lng: -118.2437, athletesCount: 4200, courtCount: 94, activeGames: 31, density: 'Extreme' },
      { id: 'h3', cityName: 'San Francisco Bay Area', lat: 37.7749, lng: -122.4194, athletesCount: 3150, courtCount: 70, activeGames: 22, density: 'Extreme' },
      { id: 'h4', cityName: 'San Diego', lat: 32.7157, lng: -117.1611, athletesCount: 1980, courtCount: 46, activeGames: 14, density: 'High' },
      { id: 'h5', cityName: 'Sacramento', lat: 38.5816, lng: -121.4944, athletesCount: 1120, courtCount: 28, activeGames: 8, density: 'High' }
    ];
  }, [city, stateCode]);

  const activeHub = useMemo(() => {
    return regionalHubs.find((h) => h.id === selectedHubId) || regionalHubs[0];
  }, [regionalHubs, selectedHubId]);

  const totalCourtsInState = useMemo(() => {
    return regionalHubs.reduce((acc, curr) => acc + curr.courtCount, 0);
  }, [regionalHubs]);

  const totalAthletesInState = useMemo(() => {
    return regionalHubs.reduce((acc, curr) => acc + curr.athletesCount, 0);
  }, [regionalHubs]);

  // Real OpenStreetMap Leaflet Map Initialization
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const centerLat = activeHub.lat;
    const centerLng = activeHub.lng;

    if (!leafletMapRef.current) {
      if ((mapContainerRef.current as any)._leaflet_id) {
        (mapContainerRef.current as any)._leaflet_id = null;
      }
      try {
        const map = L.map(mapContainerRef.current, {
          center: [centerLat, centerLng],
          zoom: stateCode === 'NY' || stateCode === 'FL' ? 7 : 6,
          zoomControl: true,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map);

        leafletMapRef.current = map;
        markersGroupRef.current = L.layerGroup().addTo(map);
      } catch (err) {
        console.warn('RegionalCoverageMap Leaflet init warning:', err);
      }
    } else {
      try {
        leafletMapRef.current.setView([centerLat, centerLng]);
      } catch (_) {}
    }

    if (markersGroupRef.current) {
      try {
        markersGroupRef.current.clearLayers();

        // Render Regional Density Hub Markers
        regionalHubs.forEach((hub) => {
          const isSel = hub.id === activeHub.id;
          const colorHex = hub.density === 'Extreme' ? '#f59e0b' : '#a3e635';

          const customHtml = `
            <div style="background-color: #090d16; border: 2px solid ${isSel ? '#ffffff' : colorHex}; border-radius: 14px; padding: 5px 10px; color: #ffffff; font-family: sans-serif; font-size: 11px; font-weight: 800; display: flex; align-items: center; gap: 6px; box-shadow: 0 4px 16px rgba(0,0,0,0.7); cursor: pointer; white-space: nowrap; transform: ${isSel ? 'scale(1.15)' : 'scale(1.0)'}; transition: transform 0.2s;">
              <span style="background-color: ${colorHex}; color: #000000; padding: 2px 6px; border-radius: 10px; font-size: 10px; font-weight: 900;">🔥 ${hub.density}</span>
              <span>${hub.cityName}</span>
              <span style="background-color: rgba(255,255,255,0.2); padding: 1px 5px; border-radius: 8px; font-size: 10px; font-weight: 800; font-family: monospace;">${hub.courtCount} Courts</span>
            </div>
          `;

          const icon = L.divIcon({
            html: customHtml,
            className: 'regional-leaflet-hub-marker',
            iconSize: [160, 34],
            iconAnchor: [80, 17],
          });

          const marker = L.marker([hub.lat, hub.lng], { icon });

          marker.bindPopup(`
            <div style="font-family: sans-serif; color: #0f172a; padding: 4px; min-width: 160px;">
              <div style="font-size: 10px; font-weight: 900; text-transform: uppercase; color: #d97706; font-family: monospace;">
                🔥 ${hub.density} Density Hub
              </div>
              <h4 style="font-size: 14px; font-weight: 900; margin: 3px 0 4px 0;">${hub.cityName}</h4>
              <p style="font-size: 11px; color: #475569; margin: 0 0 4px 0;">Athletes: <strong>${hub.athletesCount.toLocaleString()}</strong></p>
              <p style="font-size: 11px; color: #475569; margin: 0 0 6px 0;">Courts Active: <strong>${hub.courtCount}</strong></p>
            </div>
          `);

          marker.on('click', () => {
            setSelectedHubId(hub.id);
          });

          markersGroupRef.current?.addLayer(marker);
        });

        // Also render individual Court POI markers if present
        courts.forEach((court) => {
          if (court.lat && court.lng) {
            const isVerified = court.status === 'verified';
            const courtHtml = `
              <div style="background-color: #030712; border: 1.5px solid ${isVerified ? '#10b981' : '#f59e0b'}; border-radius: 10px; padding: 3px 7px; color: #ffffff; font-family: sans-serif; font-size: 10px; font-weight: 800; display: flex; align-items: center; gap: 4px; box-shadow: 0 2px 10px rgba(0,0,0,0.5); cursor: pointer; white-space: nowrap;">
                <span>${isVerified ? '🛡️' : '⏳'}</span>
                <span>${court.name.length > 12 ? court.name.slice(0, 12) + '...' : court.name}</span>
              </div>
            `;

            const courtIcon = L.divIcon({
              html: courtHtml,
              className: 'regional-court-poi-marker',
              iconSize: [120, 26],
              iconAnchor: [60, 13],
            });

            const courtMarker = L.marker([court.lat, court.lng], { icon: courtIcon });
            courtMarker.bindPopup(`
              <div style="font-family: sans-serif; color: #0f172a; padding: 4px;">
                <div style="font-size: 10px; font-weight: 900; color: ${isVerified ? '#059669' : '#d97706'}; font-family: monospace;">
                  ${isVerified ? '🛡️ Verified Court' : '⏳ Pending Review'}
                </div>
                <h5 style="font-size: 12px; font-weight: 900; margin: 2px 0;">${court.name}</h5>
                <p style="font-size: 10px; color: #475569; margin: 0;">${court.address}</p>
              </div>
            `);

            markersGroupRef.current?.addLayer(courtMarker);
          }
        });
      } catch (err) {
        console.warn('Marker rendering warning:', err);
      }
    }

    return () => {
      if (leafletMapRef.current) {
        try {
          leafletMapRef.current.remove();
        } catch (_) {}
        leafletMapRef.current = null;
      }
    };
  }, [regionalHubs, activeHub, courts, stateCode]);

  return (
    <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-slate-950 p-5 md:p-6 rounded-[2.5rem] border border-lime-400/30 shadow-xl space-y-4 relative overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-3.5">
        <div className="flex items-center space-x-2.5">
          <div className="p-2.5 bg-amber-400 text-black rounded-2xl shadow-md">
            <Layers className="w-5 h-5 stroke-[2.5]" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-amber-400/20 text-amber-300 rounded font-mono border border-amber-400/30">
                OPENSTREETMAP REGIONAL OVERLAY
              </span>
              <span className="text-[10px] text-indigo-300 font-mono">
                State of {stateName} ({stateCode})
              </span>
            </div>
            <h3 className="text-lg font-black italic uppercase text-white mt-0.5">
              Regional Coverage & Court Density Map
            </h3>
          </div>
        </div>

        {onNavigateToCourts && (
          <button
            type="button"
            onClick={onNavigateToCourts}
            className="px-3.5 py-1.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-md transition flex items-center space-x-1 shrink-0 self-start sm:self-auto"
          >
            <MapPin className="w-3.5 h-3.5 text-black" />
            <span>Interactive Court Map</span>
            <ChevronRight className="w-4 h-4 stroke-[3]" />
          </button>
        )}
      </div>

      {/* Interactive OpenStreetMap Leaflet Container */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
        {/* Real OpenStreetMap Leaflet Container */}
        <div className="lg:col-span-2 relative h-80 sm:h-96 rounded-2xl border border-white/10 overflow-hidden shadow-inner flex flex-col justify-between bg-slate-950">
          <div ref={mapContainerRef} className="w-full h-full z-0" />

          {/* Top Info Banner Overlay */}
          <div className="absolute top-3 left-3 z-10 bg-black/80 text-white px-3 py-1.5 rounded-xl border border-white/20 backdrop-blur-md text-[11px] font-mono font-black flex items-center space-x-2 shadow-lg pointer-events-none">
            <span className="w-2.5 h-2.5 rounded-full bg-lime-400 animate-pulse" />
            <span>OpenStreetMap Real Map Active ({stateName})</span>
          </div>

          {/* Bottom Legend Bar Overlay */}
          <div className="absolute bottom-3 left-3 right-3 z-10 flex items-center justify-between text-[10px] font-mono font-bold text-indigo-200 bg-black/80 p-2.5 rounded-xl border border-white/20 backdrop-blur-md shadow-xl">
            <div className="flex items-center space-x-3">
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />
                Extreme Density
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-lime-400 inline-block" />
                High Density
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
                Verified Courts
              </span>
            </div>
            <span className="hidden sm:inline">Click any hub marker to inspect</span>
          </div>
        </div>

        {/* Selected Regional Hub Breakdown Panel */}
        <div className="p-4 bg-indigo-900/60 rounded-2xl border border-white/10 space-y-3">
          <div className="flex items-center justify-between border-b border-white/10 pb-2">
            <span className="text-[10px] font-black uppercase text-amber-300 font-mono tracking-wider flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" />
              REGION INSPECTOR
            </span>
            <span className="px-2 py-0.5 bg-lime-400/20 text-lime-300 text-[9px] font-mono font-bold rounded border border-lime-400/30">
              {activeHub.density} Density
            </span>
          </div>

          <div>
            <h4 className="text-base font-black italic uppercase text-white">
              {activeHub.cityName}
            </h4>
            <p className="text-xs text-indigo-200/70 font-semibold mt-0.5">
              Primary pickup hotspot in {stateName}.
            </p>
          </div>

          {/* Key Metrics */}
          <div className="space-y-2 pt-1 font-mono text-xs">
            <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-white/10 flex items-center justify-between">
              <span className="text-indigo-300 text-[11px]">Registered Athletes</span>
              <span className="font-extrabold text-lime-400">{activeHub.athletesCount.toLocaleString()}</span>
            </div>

            <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-white/10 flex items-center justify-between">
              <span className="text-indigo-300 text-[11px]">Active Courts Listed</span>
              <span className="font-extrabold text-white">{activeHub.courtCount} Courts</span>
            </div>

            <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-white/10 flex items-center justify-between">
              <span className="text-indigo-300 text-[11px]">Pickup Games Live</span>
              <span className="font-extrabold text-amber-300">{activeHub.activeGames} Matches Today</span>
            </div>
          </div>

          {/* Statewide Summary Row */}
          <div className="pt-2 border-t border-white/10 text-[10px] text-indigo-300/80 font-mono space-y-1">
            <div className="flex justify-between">
              <span>{stateCode} State Total Courts:</span>
              <span className="text-white font-bold">{totalCourtsInState}</span>
            </div>
            <div className="flex justify-between">
              <span>{stateCode} Active Athlete Base:</span>
              <span className="text-lime-300 font-bold">{totalAthletesInState.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
